import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Card, CardContent, Typography, TextField, Button, Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, MenuItem, LinearProgress, Alert
} from '@mui/material';
import { Edit, Delete, Print, CheckCircle, Cancel, Add } from '@mui/icons-material';
import Navbar from '../setup/Navbar';
import {
    getPrinterAssignments,
    assignPrinter,
    updatePrinterStatus,
    deletePrinterAssignment,
    testPrinter,
    reset
} from '../../features/setup/printerAssignmentSlice';

export default function PrinterAssignment() {
    const dispatch = useDispatch();
    const { assignments, isLoading, isError, isSuccess, message } = useSelector(state => state.printerAssignment);

    const [showDialog, setShowDialog] = useState(false);
    const [formData, setFormData] = useState({
        categoryCode: '',
        cateDescription: '',
        printerName: '',
        printerIP: '',
        printerPort: 9100,
        printerType: 'network'
    });
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        dispatch(getPrinterAssignments());
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && message) {
            // Check if it's a test printer message (don't close dialog)
            if (message.includes('reachable') || message.includes('test')) {
                alert(message);
                dispatch(reset());
            } else {
                // For save/update/delete operations (close dialog and refresh)
                alert(message);
                dispatch(reset());
                setShowDialog(false);
                dispatch(getPrinterAssignments());
            }
        }
        if (isError) {
            alert('Error: ' + message);
            dispatch(reset());
        }
    }, [isSuccess, isError, message, dispatch]);

    const handleOpenDialog = (assignment = null) => {
        if (assignment) {
            setEditMode(true);
            setFormData({
                categoryCode: assignment.cateCode,
                cateDescription: assignment.cateDescription,
                printerName: assignment.printerName || '',
                printerIP: assignment.printerIP || '',
                printerPort: assignment.printerPort || 9100,
                printerType: assignment.printerType || 'network'
            });
        } else {
            setEditMode(false);
            setFormData({
                categoryCode: '',
                cateDescription: '',
                printerName: '',
                printerIP: '',
                printerPort: 9100,
                printerType: 'network'
            });
        }
        setShowDialog(true);
    };

    const handleSave = () => {
        if (!formData.categoryCode || !formData.printerName || !formData.printerIP) {
            alert('Please fill all required fields');
            return;
        }

        dispatch(assignPrinter({
            categoryCode: formData.categoryCode,
            printerName: formData.printerName,
            printerIP: formData.printerIP,
            printerPort: formData.printerPort,
            printerType: formData.printerType
        }));
    };

    const handleTestPrinter = () => {
        if (!formData.printerIP) {
            alert('Please enter Printer IP');
            return;
        }
        dispatch(testPrinter({
            categoryDesciption: formData.cateDescription,
            printerIP: formData.printerIP,
            printerPort: formData.printerPort
        }));
    };

    const handleToggleStatus = (assignment) => {
        const newStatus = assignment.printerStatus === 'active' ? 'inactive' : 'active';
        dispatch(updatePrinterStatus({
            assignmentID: assignment.assignmentID,
            printerStatus: newStatus
        }));
    };

    const handleDelete = (assignmentID) => {
        if (window.confirm('Are you sure you want to delete this printer assignment?')) {
            dispatch(deletePrinterAssignment(assignmentID));
        }
    };

    // Get categories without assignments for dropdown
    const availableCategories = assignments.filter(a => !a.assignmentID);

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {isLoading && <LinearProgress />}

                <Card>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">
                                KOT Printer Assignment (Category-wise)
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    if (availableCategories.length === 0) {
                                        alert('All categories are already assigned. Please use Edit button from the table to modify existing assignments.');
                                    } else {
                                        handleOpenDialog();
                                    }
                                }}
                            >
                                Assign Printer
                            </Button>
                        </Stack>

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Category</strong></TableCell>
                                    <TableCell><strong>Printer Name</strong></TableCell>
                                    <TableCell><strong>Printer IP</strong></TableCell>
                                    <TableCell><strong>Port</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignments.map((assignment) => (
                                    <TableRow key={assignment.cateCode}>
                                        <TableCell>{assignment.cateDescription}</TableCell>
                                        <TableCell>{assignment.printerName || '-'}</TableCell>
                                        <TableCell>{assignment.printerIP || '-'}</TableCell>
                                        <TableCell>{assignment.printerPort || '-'}</TableCell>
                                        <TableCell>
                                            {assignment.printerType && (
                                                <Chip label={assignment.printerType} size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {assignment.assignmentID ? (
                                                <Chip
                                                    label={assignment.printerStatus}
                                                    color={assignment.printerStatus === 'active' ? 'success' : 'default'}
                                                    size="small"
                                                    icon={assignment.printerStatus === 'active' ? <CheckCircle /> : <Cancel />}
                                                    onClick={() => handleToggleStatus(assignment)}
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <Chip label="Not Assigned" size="small" color="warning" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {assignment.assignmentID ? (
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(assignment)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(assignment.assignmentID)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Stack>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={() => handleOpenDialog(assignment)}
                                                >
                                                    Assign
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {assignments.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="textSecondary">No categories found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Assignment Dialog */}
                <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {editMode ? 'Edit Printer Assignment' : 'Assign Printer to Category'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            {!editMode && (
                                <>
                                    {availableCategories.length === 0 ? (
                                        <Alert severity="warning">
                                            All categories are already assigned. Please edit existing assignments from the table.
                                        </Alert>
                                    ) : (
                                        <TextField
                                            select
                                            label="Category *"
                                            value={formData.categoryCode}
                                            onChange={(e) => {
                                                const selected = availableCategories.find(c => c.cateCode === e.target.value);
                                                setFormData({
                                                    ...formData,
                                                    categoryCode: e.target.value,
                                                    cateDescription: selected?.cateDescription || ''
                                                });
                                            }}
                                            fullWidth
                                        >
                                            {availableCategories.map((cat) => (
                                                <MenuItem key={cat.cateCode} value={cat.cateCode}>
                                                    {cat.cateDescription}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                </>
                            )}

                            {editMode && (
                                <TextField
                                    label="Category"
                                    value={formData.cateDescription}
                                    disabled
                                    fullWidth
                                />
                            )}

                            <TextField
                                label="Printer Name *"
                                placeholder="e.g., Kitchen Printer"
                                value={formData.printerName}
                                onChange={(e) => setFormData({ ...formData, printerName: e.target.value })}
                                fullWidth
                            />

                            <TextField
                                label="Printer IP Address *"
                                placeholder="e.g., 192.168.1.100"
                                value={formData.printerIP}
                                onChange={(e) => setFormData({ ...formData, printerIP: e.target.value })}
                                fullWidth
                            />

                            <TextField
                                label="Printer Port"
                                type="number"
                                value={formData.printerPort}
                                onChange={(e) => setFormData({ ...formData, printerPort: e.target.value })}
                                fullWidth
                            />

                            <TextField
                                select
                                label="Printer Type"
                                value={formData.printerType}
                                onChange={(e) => setFormData({ ...formData, printerType: e.target.value })}
                                fullWidth
                            >
                                <MenuItem value="network">Network</MenuItem>
                                <MenuItem value="usb">USB</MenuItem>
                                <MenuItem value="shared">Shared</MenuItem>
                            </TextField>

                            <Button
                                variant="outlined"
                                startIcon={<Print />}
                                onClick={handleTestPrinter}
                                disabled={!formData.printerIP}
                            >
                                Test Connection
                            </Button>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!editMode && availableCategories.length === 0}
                        >
                            {editMode ? 'Update' : 'Assign'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
